// Portal session — sign in to the practice's portal inside Atlas (docs/portal-sessions.md).
//
// Start runs a remote browser (an ECS task) for one of the practice's portals; its
// screen arrives as a stream of JPEG frames painted onto the canvas below, and the
// mouse and keyboard on that canvas are forwarded to it. The password is typed INTO
// THE REMOTE BROWSER: this panel sends key events and characters on their way through
// and keeps none of them — no state, no log, no echo. The frame stream is painted and
// dropped. End stops the task. The key contract is in `onKeyDown` below and in the
// table in docs/portal-sessions.md ("What a key press sends").
//
// Admin and above only (the server refuses everyone else); the stream itself is for
// the super_admin or the person who started the session, so a second admin sees the
// status strip and End, not the screen.
//
// Record (super_admin, once the strip says signed in) runs atlas-billing-ops's
// recorder inside the session, scan or crawl, and polls its run every 5 s. The
// server stores the draft as the next unapproved runbook version and the catalogue
// beside it; this panel sees the version number, the counts and the log tail, and
// never holds a draft.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { retrySignIn } from "./api";
import {
  RECORDER_MODES,
  endPortalSession,
  fetchPortalSession,
  fetchPortalSessionState,
  fetchRecorderRun,
  openPortalFrames,
  sendPortalInput,
  startPortalSession,
  startRecorder,
} from "./portalSessionApi";
import { fetchAuthenticators, typeAuthenticatorIntoSession } from "./portalAuthenticatorApi";
import "./Queue.css";

const STATE_POLL_MS = 5000;
// The recorder run is polled at the same cadence while it is running; the /state
// poll above (the session's keepalive) keeps running alongside it.
const RECORDER_POLL_MS = 5000;
export const RECONNECT_MS = 2000;
const MAX_RECONNECTS = 5;
// The session browser takes about 30 s after RunTask to listen (image pull, Chromium
// launch), and until it does the frames proxy answers 502 ("did not answer") or the
// connection drops. For this long after the panel first sees a session, a frames
// error is treated like a close: wait RECONNECT_MS and ask again, outside the
// MAX_RECONNECTS count, while the /state poll keeps running so the strip flips to
// Live on its own. Three times the usual start, so a slow image pull still fits.
// After it, the panel shows the server's message and offers "Try again".
export const STARTUP_BUDGET_MS = 90000;

// Mouse buttons as the task names them.
const BUTTONS = ["left", "middle", "right"];

function clearRetry(timerRef) {
  if (timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
}

// Still inside the startup budget of the session the panel is watching
// (`startupRef.current` is `{id, at}`, set when the panel first sees a session).
export function inStartup(startupRef, now = Date.now()) {
  return !!startupRef.current && now - startupRef.current.at < STARTUP_BUDGET_MS;
}

export function modifiersOf(e) {
  const out = [];
  if (e.altKey) out.push("Alt");
  if (e.ctrlKey) out.push("Control");
  if (e.metaKey) out.push("Meta");
  if (e.shiftKey) out.push("Shift");
  return out;
}

// A printable key press: `e.key` is one character and no Alt, Control or Meta is
// held. Shift alone still counts (the uppercase letter arrives as `e.key`). The
// session browser treats alt/ctrl/meta as a shortcut and clears the text, so a key
// held with one of them is a key event, not text, whatever its `e.key`.
export function isTextKey(e) {
  return typeof e.key === "string" && e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey;
}

// A press of a modifier key on its own (`e.key` is the modifier's name). Nothing
// is sent for it: the session browser takes the modifier state from the
// `modifiers` of the key or click it is held with, and its key-code table does not
// name these keys.
export function isModifierKey(e) {
  return e.key === "Shift" || e.key === "Control" || e.key === "Alt" || e.key === "Meta";
}

// One keydown or keyup for the session browser. `key` is required there (the DOM
// KeyboardEvent.key value, which is how it finds the key code for Backspace, Enter,
// Tab, the arrows and the rest); `code` and `modifiers` ride along; never `text`,
// since a key event with text would type its character a second time.
export function keyEventOf(type, e) {
  const event = { type, key: e.key, code: e.code, modifiers: modifiersOf(e) };
  if (type === "keydown") event.repeat = !!e.repeat;
  return event;
}

export function signedInLabel(signedIn) {
  if (signedIn === true) return "signed in (guess)";
  if (signedIn === false) return "not signed in";
  return "sign-in unknown";
}

// "1:05" from a run's started_at to `now` (or to finished_at once it has one).
export function elapsedLabel(run, now = Date.now()) {
  if (!run || !run.startedAt) return "";
  const start = Date.parse(run.startedAt);
  if (Number.isNaN(start)) return "";
  const end = run.finishedAt && !Number.isNaN(Date.parse(run.finishedAt)) ? Date.parse(run.finishedAt) : now;
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

// "pages 3 · actions 12" from the recorder's counts (key names and numbers only).
export function countsLabel(counts) {
  return Object.entries(counts || {})
    .map(([key, value]) => `${key.replace(/_/g, " ")} ${value}`)
    .join(" · ");
}

// Canvas-relative coordinates in the remote page's pixels: the canvas is drawn at
// the frame's size and shown scaled to the panel's width.
export function remotePoint(canvas, clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return { x: 0, y: 0 };
  return {
    x: Math.round(((clientX - rect.left) / rect.width) * canvas.width),
    y: Math.round(((clientY - rect.top) / rect.height) * canvas.height),
  };
}

export default function PortalSessionPanel({
  practiceId,
  isSuperAdmin = false,
  readOnly = false,
  signIn = null,
  onRetried,
  currentUserId = null,
}) {
  const [overview, setOverview] = useState(null); // {enabled, portals, session}
  const [portal, setPortal] = useState("");
  const [session, setSession] = useState(null);
  const [state, setState] = useState(null);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [streamNote, setStreamNote] = useState(null);
  const [retrying, setRetrying] = useState(false);
  // Which portals have an authenticator enrolled (docs/portal-authenticators.md):
  // keys only, so the strip can offer "Type code into session" for a live portal.
  // Nothing about any code is ever here.
  const [authenticatorKeys, setAuthenticatorKeys] = useState([]);
  const [typing, setTyping] = useState(false);
  // Set when the stream has given up (startup budget spent, or MAX_RECONNECTS
  // closes): the note then offers "Try again", which restarts the stream only.
  const [streamStalled, setStreamStalled] = useState(false);
  // The recorder (super_admin, signed-in session): which mode is chosen, whether
  // the confirm line is open, and the run as the server reports it — state, counts,
  // log tail, version. The draft and the catalogue are stored server-side and are
  // never in this component (`normalizeRecorderRun` does not read them).
  const [recorderMode, setRecorderMode] = useState("scan");
  const [recorderConfirm, setRecorderConfirm] = useState(false);
  const [recorderRun, setRecorderRun] = useState(null);
  const [recorderStarting, setRecorderStarting] = useState(false);
  const [recorderError, setRecorderError] = useState(null);
  const [recorderNow, setRecorderNow] = useState(() => Date.now());
  const canvasRef = useRef(null);
  const stopRef = useRef(null);
  const reconnects = useRef(0);
  const retryTimer = useRef(null);
  // {id, at}: which session the panel is watching and when it first saw it; the
  // startup budget is measured from `at`.
  const startupRef = useRef(null);
  const alive = useRef(true);

  const live = session && (session.status === "live" || session.status === "starting");

  const load = useCallback(async () => {
    if (!practiceId) return;
    try {
      const body = await fetchPortalSession(practiceId);
      if (!alive.current) return;
      setOverview(body);
      setSession(body.session);
      setPortal((current) =>
        current && body.portals.some((p) => p.key === current)
          ? current
          : body.portals[0]?.key || "",
      );
    } catch (err) {
      if (!alive.current) return;
      setError(err.message);
    }
  }, [practiceId]);

  useEffect(() => {
    alive.current = true;
    setSession(null);
    setState(null);
    setOverview(null);
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  // ── frames ──────────────────────────────────────────────────────────────
  const paint = useCallback((b64) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      if (!canvasRef.current) return;
      if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      const ctx = canvas.getContext && canvas.getContext("2d");
      if (ctx) ctx.drawImage(img, 0, 0);
    };
    img.src = "data:image/jpeg;base64," + b64;
  }, []);

  const stopFrames = useCallback(() => {
    clearRetry(retryTimer);
    if (stopRef.current) {
      stopRef.current();
      stopRef.current = null;
    }
    setStreaming(false);
  }, []);

  const startFrames = useCallback(() => {
    if (!practiceId || stopRef.current) return;
    clearRetry(retryTimer);
    setStreamNote(null);
    setStreamStalled(false);
    const retryAfter = (note) => {
      setStreamNote(note);
      retryTimer.current = setTimeout(() => {
        retryTimer.current = null;
        if (alive.current && !stopRef.current) startFrames();
      }, RECONNECT_MS);
    };
    stopRef.current = openPortalFrames(practiceId, {
      onFrame: (b64) => {
        reconnects.current = 0;
        paint(b64);
      },
      onError: (message, status) => {
        stopRef.current = null;
        setStreaming(false);
        if (!alive.current) return;
        if (status === 403) {
          // Someone else's screen: final, whatever the session is doing.
          setStreamNote("Only the person who started this session can see its screen.");
          return;
        }
        if (inStartup(startupRef)) {
          retryAfter("Waiting for the session browser to start…");
          return;
        }
        setStreamNote(message);
        setStreamStalled(true);
      },
      onClose: () => {
        stopRef.current = null;
        setStreaming(false);
        if (!alive.current) return;
        if (inStartup(startupRef)) {
          retryAfter("Waiting for the session browser to start…");
        } else if (reconnects.current < MAX_RECONNECTS) {
          reconnects.current += 1;
          retryAfter("Reconnecting to the screen…");
        } else {
          setStreamNote("The screen stream stopped. Check the status and start again if needed.");
          setStreamStalled(true);
          load();
        }
      },
    });
    setStreaming(true);
  }, [practiceId, paint, load]);

  const sessionId = session ? session.id : null;
  const sessionStatus = session ? session.status : null;

  // The person asks for the screen again: a fresh startup budget, the stream only.
  // The session is not touched.
  const tryAgain = useCallback(() => {
    reconnects.current = 0;
    if (sessionId != null) startupRef.current = { id: sessionId, at: Date.now() };
    startFrames();
  }, [sessionId, startFrames]);

  const mayWatch =
    !!session &&
    (isSuperAdmin || (currentUserId != null && session.startedBy === currentUserId));

  // The budget belongs to a session the panel saw START (status `starting` when it
  // first appeared, by id). A session that is already live when the panel loads gets
  // none: its browser is up, so a frames error is the ordinary reconnect case.
  useEffect(() => {
    if (sessionId == null) startupRef.current = null;
    else if (startupRef.current && startupRef.current.id !== sessionId) startupRef.current = null;
    if (sessionId != null && !startupRef.current && sessionStatus === "starting") {
      startupRef.current = { id: sessionId, at: Date.now() };
    }
  }, [sessionId, sessionStatus]);

  useEffect(() => {
    if (live && mayWatch) startFrames();
    else stopFrames();
    return stopFrames;
  }, [live, mayWatch, startFrames, stopFrames]);

  // The strip flipped from Starting to Live (the /state poll got through) while the
  // stream had given up: ask for the screen once more without waiting for a click.
  const prevStatus = useRef(sessionStatus);
  useEffect(() => {
    const was = prevStatus.current;
    prevStatus.current = sessionStatus;
    if (was === "starting" && sessionStatus === "live" && streamStalled && mayWatch && !stopRef.current) {
      tryAgain();
    }
  }, [sessionStatus, streamStalled, mayWatch, tryAgain]);

  // ── state polling ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!live || !practiceId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const body = await fetchPortalSessionState(practiceId);
        if (cancelled) return;
        setSession(body.session);
        setState(body.state);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404 || err.status === 409) {
          // Gone: ended elsewhere, or the task died and the server noticed.
          setSession(null);
          setState(null);
          setNotice("The session has ended.");
          load();
        }
      }
    };
    tick();
    const timer = setInterval(tick, STATE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [live, practiceId, load]);

  // ── the recorder ────────────────────────────────────────────────────────
  const signedIn = !!state && state.signedIn === true;
  const mayRecord = isSuperAdmin && !readOnly && live && session.status === "live" && signedIn;
  const recording = !!recorderRun && recorderRun.state === "running";

  // A run another tab or worker started: when the strip first says signed in, ask
  // once whether a run is already known (404 means none).
  const askedForRun = useRef(null);
  useEffect(() => {
    if (!mayRecord || !practiceId || recorderRun || askedForRun.current === sessionId) return undefined;
    askedForRun.current = sessionId;
    let cancelled = false;
    fetchRecorderRun(practiceId)
      .then((run) => {
        if (!cancelled && run) setRecorderRun(run);
      })
      .catch(() => {
        /* no run known, or not ours to see */
      });
    return () => {
      cancelled = true;
    };
  }, [mayRecord, practiceId, recorderRun, sessionId]);

  // Poll the run every RECORDER_POLL_MS while it is running. The /state poll above is
  // untouched, so the session's keepalive runs through the recording.
  useEffect(() => {
    if (!recording || !practiceId) return undefined;
    let cancelled = false;
    const tick = async () => {
      setRecorderNow(Date.now());
      try {
        const run = await fetchRecorderRun(practiceId);
        if (cancelled) return;
        if (run) setRecorderRun(run);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404 || err.status === 409) {
          setRecorderRun(null);
          setRecorderError("The recorder run is no longer known to the session browser.");
        }
      }
    };
    const timer = setInterval(tick, RECORDER_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [recording, practiceId]);

  // The session went away: the run went with it (its draft, if the run finished, is
  // already a version).
  useEffect(() => {
    if (!live) {
      setRecorderRun(null);
      setRecorderConfirm(false);
      askedForRun.current = null;
    }
  }, [live]);

  const record = async () => {
    if (recorderStarting || !mayRecord) return;
    setRecorderStarting(true);
    setRecorderError(null);
    try {
      const run = await startRecorder(practiceId, recorderMode);
      setRecorderRun(run);
      setRecorderConfirm(false);
      setRecorderNow(Date.now());
    } catch (err) {
      setRecorderError(
        err.status === 429 ? "Too many requests; wait a minute and try again." : err.message,
      );
    } finally {
      setRecorderStarting(false);
    }
  };

  // ── which portals have an authenticator ─────────────────────────────────
  useEffect(() => {
    if (!live || !practiceId) return undefined;
    let cancelled = false;
    fetchAuthenticators(practiceId)
      .then((body) => {
        if (!cancelled) setAuthenticatorKeys(body.authenticators.map((a) => a.portalKey));
      })
      .catch(() => {
        // A biller below admin never reaches this panel; any other failure just
        // means the button is not offered, and "Show code" on the card still works.
        if (!cancelled) setAuthenticatorKeys([]);
      });
    return () => {
      cancelled = true;
    };
  }, [live, practiceId]);

  // ── actions ─────────────────────────────────────────────────────────────
  const start = async () => {
    if (starting || !portal) return;
    setStarting(true);
    setError(null);
    setNotice(null);
    reconnects.current = 0;
    try {
      const row = await startPortalSession(practiceId, portal);
      setSession(row);
    } catch (err) {
      if (err.status === 409) {
        setError("A session for this portal is already live. Reload, or end it first.");
        load();
      } else {
        setError(err.message);
      }
    } finally {
      setStarting(false);
    }
  };

  const end = async () => {
    if (ending) return;
    setEnding(true);
    setError(null);
    try {
      stopFrames();
      const row = await endPortalSession(practiceId);
      setSession(row);
      setState(null);
      setNotice("Session ended.");
    } catch (err) {
      setError(err.message);
    } finally {
      setEnding(false);
    }
  };

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const result = await retrySignIn(practiceId);
      setNotice(
        `${result.released} claim${result.released === 1 ? "" : "s"} returned to the queue; ${result.pending} waiting.`,
      );
      if (onRetried) onRetried(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setRetrying(false);
    }
  };

  // The code is computed and typed server-side; this only asks. Nothing comes back
  // but {typed: true}, so there is nothing to keep.
  const typeCode = async () => {
    if (typing || !session?.portalKey) return;
    setTyping(true);
    setError(null);
    try {
      await typeAuthenticatorIntoSession(practiceId, session.portalKey);
      setNotice("The authenticator code was typed into the session.");
      const canvas = canvasRef.current;
      if (canvas && canvas.focus) canvas.focus();
    } catch (err) {
      setError(
        err.status === 429
          ? "Too many code requests; wait a minute and try again."
          : err.message,
      );
    } finally {
      setTyping(false);
    }
  };

  // ── input forwarding — build the event, send it, keep nothing ──────────
  const forward = (event) => {
    sendPortalInput(practiceId, event).catch(() => {
      setStreamNote("An input could not be delivered to the session.");
    });
  };

  // What a key press sends (docs/portal-sessions.md has the same as a table):
  //
  // - A printable key (`isTextKey`): ONE `text` event carrying the character, and
  //   no keydown or keyup. The session browser types a `text` event with
  //   Input.insertText, and would ALSO type the character of a keydown that names
  //   a single-character key, so sending both would type it twice. Text only was
  //   chosen over "keydown with an empty text" because it is what the panel already
  //   sent and the one event that cannot double-type.
  // - A non-printable key (`e.key` longer than one character: Backspace, Delete,
  //   Tab, Enter, Escape, the arrows, Home, End, PageUp, PageDown, F-keys) and any
  //   key held with Alt, Control or Meta (a shortcut such as select-all, copy or
  //   paste inside the remote browser): a keydown and a keyup, each with `key`,
  //   `code` and `modifiers`, and no `text`. The browser needs `key` to find the
  //   key code; without it the browser refuses the event and the panel reports
  //   that the input could not be delivered.
  // - A modifier key on its own (`isModifierKey`): nothing.
  //
  // Nothing about the key is kept: the event is built, sent and dropped.
  const onKeyDown = (e) => {
    if (!live || !mayWatch) return;
    // The browser must not act on the key itself (Tab moving focus, Backspace
    // navigating, space scrolling); the remote page gets it instead. Control/Meta
    // shortcuts other than the editing ones are left to this browser.
    if (!(e.ctrlKey || e.metaKey) || ["KeyA", "KeyC", "KeyV", "KeyX", "KeyZ"].includes(e.code)) {
      e.preventDefault();
    }
    if (isTextKey(e)) {
      forward({ type: "text", text: e.key });
      return;
    }
    if (isModifierKey(e)) return;
    forward(keyEventOf("keydown", e));
  };

  const onKeyUp = (e) => {
    if (!live || !mayWatch) return;
    e.preventDefault();
    // The release of a printable key or a bare modifier was not sent as a keydown,
    // so it gets no keyup either.
    if (isTextKey(e) || isModifierKey(e)) return;
    forward(keyEventOf("keyup", e));
  };

  const onClick = (e) => {
    if (!live || !mayWatch) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas && canvas.focus) canvas.focus();
    forward({
      type: "click",
      ...remotePoint(canvas, e.clientX, e.clientY),
      button: BUTTONS[e.button] || "left",
      clickCount: e.detail || 1,
      modifiers: modifiersOf(e),
    });
  };

  const onWheel = (e) => {
    if (!live || !mayWatch) return;
    forward({
      type: "wheel",
      ...remotePoint(canvasRef.current, e.clientX, e.clientY),
      deltaX: Math.round(e.deltaX),
      deltaY: Math.round(e.deltaY),
    });
  };

  // ── render ───────────────────────────────────────────────────────────────
  if (!practiceId) return null;

  const portals = overview ? overview.portals : [];
  const enabled = overview ? overview.enabled : true;
  const portalLabel = portals.find((p) => p.key === (session?.portalKey || portal))?.label;

  return (
    <section className="sq-ps" data-testid="portal-session">
      <h3 className="sq-section-title">Portal session</h3>
      <p className="sq-muted">
        Sign in to the practice’s portal inside Atlas. The browser runs on Atlas’s
        side and keeps its device trust between sessions; the password is typed into
        that browser and nowhere else. One session per portal at a time.
      </p>

      {error && (
        <div className="sq-message error" role="alert">
          <span>{error}</span>
          <button className="sq-message-close" aria-label="Dismiss" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}
      {notice && (
        <div className="sq-message success" role="status">
          <span>{notice}</span>
          <button className="sq-message-close" aria-label="Dismiss" onClick={() => setNotice(null)}>
            ×
          </button>
        </div>
      )}

      {overview && !enabled && (
        <div className="sq-run-banner grey" role="status">
          <span>Portal sessions are not enabled for this environment.</span>
        </div>
      )}

      {overview && enabled && portals.length === 0 && !live && (
        <div className="sq-run-banner grey" role="status">
          <span>This practice has no portal to sign in to yet (no submit portal, no runbook).</span>
        </div>
      )}

      {!live && enabled && portals.length > 0 && (
        <div className="sq-ps-start">
          <label className="sq-field">
            <span>Portal</span>
            <select
              value={portal}
              onChange={(e) => setPortal(e.target.value)}
              disabled={starting || readOnly}
              data-testid="portal-select"
            >
              {portals.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          {!readOnly && (
            <button className="btn btn-primary btn-sm" onClick={start} disabled={starting || !portal}>
              {starting ? "Starting…" : "Start session"}
            </button>
          )}
        </div>
      )}

      {live && (
        <>
          <div className="sq-ps-strip" data-testid="portal-session-status">
            <span className={`sq-status ${session.status === "live" ? "sq-status-submitted" : "sq-status-leased"}`}>
              {session.status === "live" ? "Live" : "Starting"}
            </span>
            <span>{portalLabel || session.portalKey}</span>
            <span className="sq-muted">
              {state ? `idle ${state.idleSeconds ?? "–"}s` : "waiting for the browser…"}
            </span>
            <span className="sq-muted">{state ? signedInLabel(state.signedIn) : ""}</span>
            {state?.host && <span className="sq-muted">{state.host}</span>}
            {session.startedByEmail && (
              <span className="sq-muted">started by {session.startedByEmail}</span>
            )}
            <span className="sq-ps-actions">
              {mayWatch && !readOnly && authenticatorKeys.includes(session.portalKey) && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={typeCode}
                  disabled={typing || session.status !== "live"}
                  title="Type the portal's current authenticator code into the session, without showing it"
                  data-testid="type-code"
                >
                  {typing ? "Typing…" : "Type code into session"}
                </button>
              )}
              {mayRecord && !recording && !recorderConfirm && (
                <span className="sq-ps-record" data-testid="record-control">
                  <select
                    value={recorderMode}
                    onChange={(e) => setRecorderMode(e.target.value)}
                    aria-label="Recorder mode"
                    data-testid="record-mode"
                  >
                    {RECORDER_MODES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setRecorderError(null);
                      setRecorderConfirm(true);
                    }}
                    title="Run the recorder inside this signed-in session; its draft becomes the next runbook version, unapproved"
                    data-testid="record"
                  >
                    Record
                  </button>
                </span>
              )}
              {signIn?.needsSignIn && !readOnly && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={retry}
                  disabled={retrying}
                  title="Return the stopped run's claims to the queue now that the portal is signed in"
                >
                  {retrying ? "Retrying…" : "Retry the queue"}
                </button>
              )}
              {!readOnly && (
                <button className="btn btn-secondary btn-sm" onClick={end} disabled={ending}>
                  {ending ? "Ending…" : "End session"}
                </button>
              )}
            </span>
          </div>

          {mayRecord && recorderConfirm && !recording && (
            <div className="sq-ps-recorder" data-testid="record-confirm">
              <span>
                Reads only: the recorder clicks menus and pagers under the dry port; nothing is
                typed or saved.
              </span>
              <span className="sq-ps-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={record}
                  disabled={recorderStarting}
                  data-testid="record-start"
                >
                  {recorderStarting ? "Starting…" : `Start ${recorderMode}`}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setRecorderConfirm(false)}
                  disabled={recorderStarting}
                >
                  Cancel
                </button>
              </span>
            </div>
          )}

          {recorderError && (
            <div className="sq-message error" role="alert">
              <span>{recorderError}</span>
              <button className="sq-message-close" aria-label="Dismiss" onClick={() => setRecorderError(null)}>
                ×
              </button>
            </div>
          )}

          {recorderRun && (
            <div className="sq-ps-recorder" data-testid="record-progress">
              <span className={`sq-status ${recorderRun.state === "done" ? "sq-status-submitted" : recorderRun.state === "failed" ? "sq-status-held" : "sq-status-leased"}`}>
                {recorderRun.state}
              </span>
              <span>recorder {recorderRun.mode}</span>
              <span className="sq-muted">{elapsedLabel(recorderRun, recorderNow)}</span>
              {recorderRun.state !== "running" && Object.keys(recorderRun.counts).length > 0 && (
                <span className="sq-muted">{countsLabel(recorderRun.counts)}</span>
              )}
              {recorderRun.state === "failed" && (
                <span className="sq-muted">
                  {recorderRun.exitCode != null ? `exit ${recorderRun.exitCode}` : "no draft"}
                </span>
              )}
              {recorderRun.state === "done" && recorderRun.version != null && (
                <span data-testid="record-saved">Draft saved as version {recorderRun.version}</span>
              )}
              {recorderRun.problem && <span className="sq-muted">{recorderRun.problem}</span>}
              {recorderRun.state !== "running" && (
                <button
                  className="sq-link"
                  onClick={() => {
                    setRecorderRun(null);
                    setRecorderError(null);
                  }}
                >
                  Dismiss
                </button>
              )}
              {recorderRun.logTail && (
                <details className="sq-ps-recorder-log">
                  <summary>Log</summary>
                  <pre>{recorderRun.logTail}</pre>
                </details>
              )}
            </div>
          )}

          {mayWatch ? (
            <div className="sq-ps-screen">
              <canvas
                ref={canvasRef}
                className="sq-ps-canvas"
                tabIndex={0}
                width={1280}
                height={800}
                aria-label="The portal, live. Click to focus, then type."
                data-testid="portal-canvas"
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                onClick={onClick}
                onContextMenu={onClick}
                onWheel={onWheel}
              />
              <div className="sq-ps-note sq-muted">
                {streaming ? "Click the screen to focus it, then type as you would in the portal." : streamNote || "Connecting to the screen…"}
                {streaming && streamNote ? ` ${streamNote}` : ""}
                {!streaming && streamStalled && (
                  <>
                    {" "}
                    <button className="sq-link" onClick={tryAgain} data-testid="frames-try-again">
                      Try again
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="sq-run-banner grey" role="status">
              <span>
                Only the person who started this session can see its screen. You can end it.
              </span>
            </div>
          )}
        </>
      )}

      {!live && signIn?.needsSignIn && !readOnly && enabled && (
        <p className="sq-muted sq-ps-hint">
          The queue stopped at the portal’s sign-in. Start a session, sign in, then press
          “Retry the queue” on the strip, or <button className="sq-link" onClick={retry} disabled={retrying}>retry now</button> if you signed in elsewhere.
        </p>
      )}
    </section>
  );
}
